// Simplified Reports Page - Following Tour Management layout
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

import { reportsService } from '../modules/reports/services/reportsService';
import { DATA_TYPE_LABELS, REPORT_TYPE_LABELS, DataType, ReportType } from '../modules/reports/types';
import { useAppSelector } from '../store/hooks';
import { selectSelectedMuseum } from '../store/slices/museumSlice';

interface ReportData {
  id: number;
  template: {
    name: string;
    report_type: string;
  };
  museum: string;
  report_date: string;
  generated_at: string;
  file_format: string;
  file_path: string;
  file_size: number;
  generated_by: {
    username: string;
  };
  report_source: 'periodic' | 'customized';
  sections?: ReportSection[];
}

interface ReportSection {
  title: string;
  data: Record<string, string | number | boolean | null>;
}

interface GeneratedReportRaw {
  id: number;
  template?: number;
  template_name?: string;
  template_data?: {
    id?: number;
    name?: string;
    report_type?: string;
  };
  museum: string;
  report_date: string;
  generated_at: string;
  file_format: string;
  file_path: string;
  file_size: number;
  generated_by?: number;
  generated_by_username?: string;
  report_source?: string;
  date_from?: string;
  date_to?: string;
}

interface MuseumOption {
  id: number;
  name: string;
  museum_code: string;
}

const resolveReportType = (report: GeneratedReportRaw) => {
  const rawName = report.template_data?.name || report.template_name || '';
  const rawType = report.template_data?.report_type;
  const detectedDataType =
    (Object.keys(DATA_TYPE_LABELS) as DataType[]).find(dt => rawName.startsWith(dt + '_')) ||
    (Object.keys(DATA_TYPE_LABELS) as DataType[]).find(dt => report.file_path.includes(`_${dt}_`)) ||
    (Object.keys(DATA_TYPE_LABELS) as DataType[]).find(dt => report.file_path.includes(`${dt}_`));
  const resolvedType = detectedDataType || rawType || 'customized_report';
  const displayType =
    (detectedDataType && DATA_TYPE_LABELS[detectedDataType]) ??
    DATA_TYPE_LABELS[rawType as DataType] ??
    REPORT_TYPE_LABELS[rawType as ReportType] ??
    rawType ??
    'Unknown';

  return { displayType, resolvedType, rawName };
};

const ReportsPage: React.FC = () => {
  // Get current user from Redux store
  const { user } = useAppSelector(state => state.auth);
  const isAdmin = user?.role === 'admin';
  const isTechSupport = user?.role === 'tech_support';
  const canGenerateReports = Boolean(user);
  const selectedMuseum = useAppSelector(selectSelectedMuseum);
  const allocatedMuseumCode = selectedMuseum?.museum_code || '';
  const allocatedMuseumLabel = selectedMuseum
    ? `${selectedMuseum.name || selectedMuseum.museum_code} (${selectedMuseum.museum_code})`
    : 'No museum selected';
  const canChooseMuseumInDialog = (isAdmin || isTechSupport) && !selectedMuseum;

  // Museums fetched from API
  const [museums, setMuseums] = useState<MuseumOption[]>([]);

  // Dialog state
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    museum: selectedMuseum?.museum_code || '',
    data_type: 'tour_reports' as DataType,
    month: new Date(),
    file_format: 'csv' as 'csv' | 'pdf',
  });

  // Report data state
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [generating, setGenerating] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [selectedReportForView, setSelectedReportForView] = useState<ReportData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [reportPendingDelete, setReportPendingDelete] = useState<ReportData | null>(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Fetch museums from API
  useEffect(() => {
    const fetchMuseums = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/museums/', {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (response.ok) {
          const data = await response.json();
          setMuseums(data.results || data);
        }
      } catch (err) {
        console.error('Failed to fetch museums:', err);
      }
    };
    fetchMuseums();
  }, []);

  // Sync museum selector with Redux selectedMuseum
  useEffect(() => {
    if (selectedMuseum?.museum_code) {
      setFormData(prev => ({ ...prev, museum: selectedMuseum.museum_code }));
      return;
    }

    if (canChooseMuseumInDialog) {
      setFormData(prev => ({ ...prev, museum: '' }));
      return;
    }

    setFormData(prev => ({ ...prev, museum: allocatedMuseumCode }));
  }, [allocatedMuseumCode, canChooseMuseumInDialog, selectedMuseum]);

  // Fetch existing reports
  const fetchExistingReports = async () => {
    try {
      setLoadingData(true);
      const reports = await reportsService.getGeneratedReports(selectedMuseum?.museum_code);

      const formattedReports: ReportData[] = (reports as GeneratedReportRaw[]).map(report => {
        const { displayType, resolvedType, rawName } = resolveReportType(report);
        const generatedFileName = report.file_path.split('/').pop() || '';
        const displayName = generatedFileName || rawName || `Report #${report.id}`;

        return {
          id: report.id,
          template: {
            name: displayName,
            report_type: resolvedType,
          },
          museum: report.museum,
          report_date: report.report_date,
          generated_at: report.generated_at,
          file_format: report.file_format,
          file_path: report.file_path,
          file_size: report.file_size,
          generated_by: {
            username: report.generated_by_username || 'System',
          },
          report_source: report.report_source === 'periodic' ? 'periodic' : 'customized',
          sections: [
            {
              title: 'Report Information',
              data: {
                'Report Type': displayType,
                Museum: report.museum,
                Month: report.date_from ? report.date_from.slice(0, 7) : '—',
                'Generated At': report.generated_at,
                'File Format': report.file_format,
                'File Size': `${report.file_size} bytes`,
              },
            },
          ],
        };
      });
      setReportData(formattedReports);
    } catch (error) {
      console.error('Failed to fetch existing reports:', error);
      setSnackbarMessage('Failed to load existing reports');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingData(false);
    }
  };

  // Fetch existing reports when component mounts or selected museum changes
  useEffect(() => {
    fetchExistingReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMuseum]);

  // Handle form input changes
  const handleInputChange = (field: keyof typeof formData, value: string | Date | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value ?? prev[field],
    }));
  };

  // Generate report data using real API
  const handleGenerateReport = async () => {
    if (!formData.museum) {
      setSnackbarMessage('Please select a museum before generating a report.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setGenerating(true);

      // Call the real API to generate report
      const request = {
        report_type: 'customized_report' as const,
        data_type: formData.data_type,
        report_source: 'customized' as const,
        museum: formData.museum,
        month: format(formData.month, 'yyyy-MM'),
        file_format: formData.file_format,
      };

      await reportsService.generateReport(request);

      await fetchExistingReports();
      setSnackbarMessage('Report generated successfully!');
      setSnackbarSeverity('success');
      setGenerateDialogOpen(false);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to generate report';
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
    } finally {
      setGenerating(false);
    }
    setSnackbarOpen(true);
  };

  // Handle view report
  const handleViewReport = (report: ReportData) => {
    setSelectedReportForView(report);
  };

  useEffect(() => {
    let isActive = true;
    let objectUrl: string | null = null;

    const loadPreview = async () => {
      if (!selectedReportForView) {
        setPreviewError(null);
        setPreviewUrl(null);
        return;
      }

      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewUrl(null);

      try {
        if (selectedReportForView.file_format !== 'pdf') {
          setPreviewLoading(false);
          return;
        }

        const { file } = await reportsService.exportReport(selectedReportForView.id, selectedReportForView.file_format);

        if (!isActive) {
          return;
        }

        if (selectedReportForView.file_format === 'pdf') {
          objectUrl = window.URL.createObjectURL(file);
          setPreviewUrl(objectUrl);
          return;
        }
      } catch (error: unknown) {
        if (!isActive) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Failed to load preview';
        setPreviewError(message);
      } finally {
        if (isActive) {
          setPreviewLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      isActive = false;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [selectedReportForView]);

  // Handle download — uses the format the report was originally generated with
  const handleDownload = async (report: ReportData) => {
    try {
      setDownloadingId(report.id);
      const { file, filename } = await reportsService.exportReport(report.id, report.file_format);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSnackbarMessage(`Report downloaded as ${report.file_format.toUpperCase()}`);
      setSnackbarSeverity('success');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to download report';
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
    } finally {
      setDownloadingId(null);
    }
    setSnackbarOpen(true);
  };

  const handleDeleteReport = async () => {
    if (!reportPendingDelete) {
      return;
    }

    try {
      setDeletingId(reportPendingDelete.id);
      await reportsService.deleteReport(reportPendingDelete.id);
      if (selectedReportForView?.id === reportPendingDelete.id) {
        setSelectedReportForView(null);
      }
      setReportPendingDelete(null);
      await fetchExistingReports();
      setSnackbarMessage('Report deleted successfully');
      setSnackbarSeverity('success');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete report';
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
    } finally {
      setDeletingId(null);
      setSnackbarOpen(true);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Reports
          </Typography>
          {canGenerateReports && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => setGenerateDialogOpen(true)}>
                GENERATE REPORT
              </Button>
            </Box>
          )}
        </Box>

        {/* Report Data Table */}
        {loadingData ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading report data...</Typography>
          </Box>
        ) : reportData.length > 0 ? (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Report Data ({reportData.length} items)
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Report Name</TableCell>
                      <TableCell>Museum / Report Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Month</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.map(row => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2">{row.template.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.museum}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {REPORT_TYPE_LABELS[row.template.report_type as ReportType] ??
                              DATA_TYPE_LABELS[row.template.report_type as DataType] ??
                              row.template.report_type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label="Completed" color="primary" size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.report_source === 'periodic' ? 'Periodic' : 'Customized'}
                            color={row.report_source === 'periodic' ? 'primary' : 'secondary'}
                            size="small"
                            variant={row.report_source === 'periodic' ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(row.report_date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                            })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(row.generated_at).toLocaleString('en-SG', {
                              timeZone: 'Asia/Singapore',
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="View Report">
                              <IconButton size="small" onClick={() => handleViewReport(row)}>
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={`Download ${row.file_format.toUpperCase()}`}>
                              <IconButton
                                size="small"
                                onClick={() => handleDownload(row)}
                                disabled={downloadingId === row.id}
                              >
                                {downloadingId === row.id ? <CircularProgress size={16} /> : <DownloadIcon />}
                              </IconButton>
                            </Tooltip>
                            {isAdmin && (
                              <Tooltip title="Delete Report">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setReportPendingDelete(row)}
                                  disabled={deletingId === row.id}
                                >
                                  {deletingId === row.id ? <CircularProgress size={16} /> : <DeleteIcon />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AssessmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Report Data
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {canGenerateReports
                    ? 'Select parameters and click "Generate Report" to view data'
                    : 'No generated reports are available for your museum.'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Generate Report Dialog */}
        <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Generate Report</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select the type of report you want to export and configure the parameters.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={formData.data_type}
                  label="Report Type"
                  onChange={e => handleInputChange('data_type', e.target.value)}
                >
                  <MenuItem value="tour_reports">Guided Tour User Report</MenuItem>
                  <MenuItem value="concierge_logs">Concierge Service User Report</MenuItem>
                  <MenuItem value="content_reports">Content Report</MenuItem>
                  <MenuItem value="robot_utilization" disabled>
                    Robot Utilization &amp; Service Report (coming soon)
                  </MenuItem>
                </Select>
              </FormControl>

              {canChooseMuseumInDialog ? (
                <FormControl fullWidth>
                  <InputLabel>Museum</InputLabel>
                  <Select
                    value={formData.museum}
                    label="Museum"
                    onChange={e => handleInputChange('museum', e.target.value)}
                  >
                    {museums.map(m => (
                      <MenuItem key={m.id} value={m.museum_code}>
                        {m.name} ({m.museum_code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label="Museum"
                  value={allocatedMuseumLabel}
                  disabled
                  helperText={
                    selectedMuseum?.museum_code
                      ? 'Using the museum selected in the sidebar for this export.'
                      : allocatedMuseumCode
                        ? 'Using your allocated museum for this export.'
                        : 'No museum selected. Choose a museum from the sidebar first.'
                  }
                />
              )}

              <DatePicker
                label="Month"
                value={formData.month}
                onChange={date => handleInputChange('month', date)}
                views={['year', 'month']}
                openTo="month"
                slotProps={{ textField: { fullWidth: true, helperText: 'Select the month to report on' } }}
              />

              <FormControl fullWidth>
                <InputLabel>File Format</InputLabel>
                <Select
                  value={formData.file_format}
                  label="File Format"
                  onChange={e => handleInputChange('file_format', e.target.value)}
                >
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="pdf">PDF</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGenerateDialogOpen(false)} disabled={generating}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerateReport}
              variant="contained"
              startIcon={<RefreshIcon />}
              disabled={generating || !formData.museum}
            >
              {generating ? 'Generating...' : 'Generate Report'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Report Dialog */}
        <Dialog open={!!selectedReportForView} onClose={() => setSelectedReportForView(null)} maxWidth="md" fullWidth>
          <DialogTitle>Report Details</DialogTitle>
          <DialogContent>
            {selectedReportForView && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedReportForView.template.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Museum & Type
                    </Typography>
                    <Typography variant="body2" sx={{ maxWidth: 300 }}>
                      {selectedReportForView.museum} -{' '}
                      {REPORT_TYPE_LABELS[selectedReportForView.template.report_type as ReportType] ??
                        DATA_TYPE_LABELS[selectedReportForView.template.report_type as DataType] ??
                        selectedReportForView.template.report_type}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip label="Completed" color="primary" size="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Source
                    </Typography>
                    <Chip
                      label={selectedReportForView.report_source === 'periodic' ? 'Periodic' : 'Customized'}
                      color={selectedReportForView.report_source === 'periodic' ? 'primary' : 'secondary'}
                      size="small"
                      variant={selectedReportForView.report_source === 'periodic' ? 'filled' : 'outlined'}
                    />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Month
                    </Typography>
                    <Typography variant="body2">
                      {new Date(selectedReportForView.report_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                      })}
                    </Typography>
                  </Box>
                </Box>

                {/* Report sections */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Report Sections ({selectedReportForView.sections?.length || 0})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {selectedReportForView.sections?.map((section, index) => (
                      <Paper key={index} sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                          {section.title}
                        </Typography>
                        <Box
                          sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}
                        >
                          {Object.entries(section.data).map(([key, value]) => (
                            <Box key={key}>
                              <Typography variant="body2" color="text.secondary">
                                {key}
                              </Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Box>

                {selectedReportForView.file_format === 'pdf' && (
                  <>
                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        PDF Preview
                      </Typography>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        This is a preview only. You cannot edit the report here. Download the file to edit it locally.
                      </Alert>

                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          minHeight: 320,
                          maxHeight: 480,
                          overflow: 'auto',
                          backgroundColor: 'grey.50',
                        }}
                      >
                        {previewLoading ? (
                          <Box display="flex" justifyContent="center" alignItems="center" minHeight="280px" gap={2}>
                            <CircularProgress size={24} />
                            <Typography variant="body2">Loading PDF preview...</Typography>
                          </Box>
                        ) : previewError ? (
                          <Alert severity="error">{previewError}</Alert>
                        ) : previewUrl ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Preview only. PDF editing is disabled in this window.
                            </Typography>
                            <Box
                              component="object"
                              data={previewUrl}
                              type="application/pdf"
                              aria-label={`${selectedReportForView.template.name} preview`}
                              sx={{
                                width: '100%',
                                height: 380,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                backgroundColor: 'common.white',
                              }}
                            >
                              <Box sx={{ p: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  PDF preview is unavailable in this browser window. Use Download to open the file
                                  locally.
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Preview unavailable.
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedReportForView(null)}>Close</Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => {
                if (selectedReportForView) {
                  handleDownload(selectedReportForView);
                }
              }}
            >
              Download {selectedReportForView?.file_format.toUpperCase()}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Report Dialog */}
        <Dialog
          open={!!reportPendingDelete}
          onClose={() => {
            if (!deletingId) {
              setReportPendingDelete(null);
            }
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Report</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Delete {reportPendingDelete?.template.name}? This removes the generated report record and stored file.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReportPendingDelete(null)} disabled={!!deletingId}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteReport}
              color="error"
              variant="contained"
              startIcon={deletingId ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
              disabled={!!deletingId}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default ReportsPage;
